<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { Drag, Drop } from 'vue-drag-drop';
import {
  clientProgramComputed,
  clientProgramMethods,
  exerciseDifficultyMethods,
  authComputed,
  muscleGroupMethods,
  workoutMethods,
  exerciseMethods,
  exerciseDifficultyComputed,
  muscleGroupComputed,
  workoutComputed,
  exerciseComputed,
  programComputed,
} from '@state/helpers';

// lazy loaded!
const CalendarSlotBasic = () =>
  import(
    /* webpackChunkName: "CalendarSlotBasic" */ '@components/programs/calendar-slots/calendar-slot-basic.vue'
  );
const NotificationProgramCalendar = () =>
  import('@components/notifications/notification-program-calendar.vue');
const CalendarTooltipActionList = () =>
  import(
    '@components/programs/calendar-slots/calendar-tooltip-action-list.vue'
  );
const CalendarSlotTooltip = () =>
  import('@components/programs/calendar-slots/calendar-slot-tooltip.vue');
const CalendarSlotCopy = () =>
  import('@components/programs/calendar-slots/calendar-slot-copy.vue');

export default {
  components: {
    CalendarSlotBasic,
    CalendarSlotCopy,
    CalendarSlotTooltip,
    CalendarTooltipActionList,
    NotificationProgramCalendar,
    Drag,
    Drop,
  },
  props: {
    isClientProgram: {
      type: Boolean,
      required: true,
    },
    programWeeks: {
      type: Array,
      required: true,
      default: () => [],
    },
    activateWeekDuplication: {
      type: Boolean,
      required: true,
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      deletingCheckin: '',
      viewActions: false,
      duplicationDayActive: false,
      dayMouseOver: {},
      weekMouseOver: {},
      duplicationData: {},
      copyingDay: false,
      copyingWeek: false,
      dragenterDayNum: null,
      dragenterWeekNum: null,
      movingItem: false,
      dragWeekOver: null,
      workoutPage: 1,
      moreWorkoutPages: true,
    };
  },
  computed: {
    ...clientProgramComputed,
    ...exerciseDifficultyComputed,
    ...muscleGroupComputed,
    ...workoutComputed,
    ...exerciseComputed,
    ...authComputed,
    ...programComputed,
  },
  async beforeMount() {
    try {
      do {
        this.workoutPage = this.workoutPage + 1;
        await this.getAllWorkouts({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
          page: this.workoutPage,
        })
          .then((res) => {
            this.moreWorkoutPages = res?.programWorkouts?.length > 0;
          })
          .catch((err) => {
            console.warn(err);
          });
      } while (this.moreWorkoutPages);

      if (!this.allExercises || !this.allExercises.length) {
        await this.getAllExercises({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
      }

      if (
        !this.allExerciseDifficulties ||
        !this.allExerciseDifficulties.length
      ) {
        await this.getAllExerciseDifficulties({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
      }

      if (!this.allMuscleGroups || !this.allMuscleGroups.length) {
        await this.getAllMuscleGroups({
          orgId: this.userOrgId,
          trainerId: this.trainerId ? this.trainerId : '0',
        });
      }
    } catch (e) {
      console.log(e);
    }
  },
  mounted() {
    if (this.isClientProgram) {
      this.scrollToCurrentWeek();
    }
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (
        modalId === 'drag-confirmation-modal' &&
        this.dragModalCtx &&
        this.dragModalCtx.confirm
      ) {
        this.handleDropWorkout(
          this.dragModalCtx.targetedDataClone,
          this.dragModalCtx.destinationDataClone
        );
      }
    });
  },
  methods: {
    ...muscleGroupMethods,
    ...exerciseDifficultyMethods,
    ...clientProgramMethods,
    ...workoutMethods,
    ...exerciseMethods,
    addWorkout(day, week) {
      this.viewActions = false;
      this.$emit('add-workout', day, week);
    },
    createNote() {
      this.viewActions = false;
      this.$emit('create-note');
    },
    viewWorkout(workout, day, week) {
      this.viewActions = false;
      this.$emit('view-workout', workout, day, week);
    },
    viewNote(day, week) {
      this.viewActions = false;
      this.$emit('view-note', day, week);
    },
    createCheckin(day, week, targetedDataClone, action) {
      this.viewActions = false;
      const targetedDay = day.dayNumber;
      const targetedWeek = week.weekNumber;
      const programId = week.program_id;

      const payload = {
        source: {
          type: 'checkIn',
        },
        programDestination: {
          weekNumber: targetedWeek,
          dayNumber: targetedDay,
        },
      };
      const sameWeek = targetedDataClone?.week?.weekNumber === targetedWeek;
      const checkinObj = {
        programId,
        payload,
        targetedDataClone,
        action,
        sameWeek,
      };
      if (targetedDay && targetedWeek && programId) {
        try {
          return this.addUpdateCheckin(checkinObj);
        } catch (e) {
          console.log(e);
        }
      }
    },
    async deleteCheckin(checkinItem, day, week) {
      this.deletingCheckin = checkinItem.scheduledCheckIn_id;
      const dayId = day.programDay_id;
      const weekId = week._id;
      const programId = week.program_id;
      const checkinId = checkinItem.scheduledCheckIn_id;

      const payload = { programId, weekId, dayId, checkinId };

      if (dayId && weekId && programId && checkinId) {
        await this.deleteCheckinDay(payload)
          .then((res) => {
            if (res && res.status === 200) {
              this.deletingCheckin = '';
            } else {
              this.deletingCheckin = '';
            }
          })
          .catch((e) => console.log(e));
      }
    },
    getDayNumber(weekNumber, dayNumber) {
      switch (dayNumber) {
        case 1:
          return 'Mon';
        case 2:
          return 'Tue';
        case 3:
          return 'Wed';
        case 4:
          return 'Thu';
        case 5:
          return 'Fri';
        case 6:
          return 'Sat';
        case 7:
          return 'Sun';
        default:
          return '---';
      }
    },
    getDayName(week, day) {
      if (day && week && week.weekOf) {
        return moment
          .utc(week.weekOf)
          .add(day.dayNumber - 1, 'days')
          .format('dddd');
      }
    },
    highlightAllWeek(e, week) {
      if (this.selectedAllWeek && this.selectedAllWeek.weekOf) {
        const prevCheck = document.getElementById(this.selectedAllWeek.weekOf);
        prevCheck.checked = false;
      }
      if (e.target.checked) {
        this.setSelectedAllWeek(week);
      }
      if (!e.target.checked) {
        this.setSelectedAllWeek(null);
        this.$emit('set-activate-week-duplication', false);
      }
    },
    toggleActionList(week, day) {
      const calculatedDayNumber = this.getDayNumber(
        week.weekNumber,
        day.dayNumber
      );
      if (isEqual({ ...day, calculatedDayNumber }, this.selectedDay)) {
        if (this.viewActions === false) {
          this.viewActions = true;
        } else {
          this.viewActions = false;
          this.setSelectedDay(null);
          this.setSelectedWeek(null);
        }
      } else {
        this.viewActions = true;
        this.setSelectedDay({ ...day, calculatedDayNumber });
        this.setSelectedWeek(week);
      }
    },
    duplicateDayCancel() {
      this.duplicationDayActive = false;
    },
    duplicateDayStart(day, week) {
      this.viewActions = false;
      this.duplicationDayActive = true;
      this.duplicationData = { day, week };
    },
    async duplicateDayEnd(day, week) {
      this.copyingDay = true;
      const programId = this.duplicationData.week.program_id;
      const sourceDay = this.duplicationData.day.dayNumber;
      const sourceWeek = this.duplicationData.week.weekNumber;
      const destDay = day.dayNumber;
      const destWeek = week.weekNumber;

      const payload = {
        toProgramId: week.program_id,
        payload: {
          source: {
            program_id: programId,
            weekNumber: sourceWeek,
            dayNumber: sourceDay,
          },
          programDestination: {
            weekNumber: destWeek,
            dayNumber: destDay,
          },
        },
      };

      await this.duplicateToADay(payload)
        .then((res) => {
          this.viewActions = false;
          this.duplicationDayActive = false;
          this.duplicationData = {};
          this.dayMouseOver = {};
          this.weekMouseOver = {};
          this.copyingDay = false;
        })
        .catch((e) => console.log(e));
    },
    async onClearAllDayEvents(day, week) {
      this.viewActions = false;
      const programId = week.program_id;
      const weekId = week._id;
      const dayId = day.programDay_id;

      const payload = { programId, weekId, dayId };
      await this.clearAllDayEvents(payload);
    },
    async onDuplicateToAWeek(week) {
      this.copyingWeek = true;
      const payload = {
        toProgramId: this.selectedAllWeek.program_id,
        payload: {
          source: {
            program_id: this.selectedAllWeek.program_id,
            weekNumber: this.selectedAllWeek.weekNumber,
          },
          programDestination: {
            weekNumber: week.weekNumber,
          },
        },
      };

      await this.duplicateToAWeek(payload)
        .then((res) => {
          this.copyingWeek = false;
          this.setSelectedAllWeek(null);
          this.$emit('set-activate-week-duplication', false);
        })
        .catch((e) => console.log(e));
    },
    onClearAllWeekEvents(week) {
      this.$emit('on-clear-all-week-events', week);
    },
    dayHasContent(day, week) {
      if (
        day.workouts.some(
          (workout) => workout.hasWorkout || workout.programWorkout_id
        ) ||
        day.dayHasTrainerNote ||
        day.checkIns.some((checkinItem) => checkinItem.scheduledCheckIn_id)
      ) {
        return true;
      } else {
        return false;
      }
    },
    isEqualData(day, week) {
      return (
        isEqual(this.dayMouseOver, day) &&
        isEqual(this.weekMouseOver, week) &&
        !isEqual(this.duplicationData.day, day)
      );
    },
    viewTrashIcon() {
      this.viewTrash = !this.viewTrash;
    },
    isDuplicateToThisWeek(week) {
      return (
        this.selectedAllWeek &&
        week.weekOf !== this.selectedAllWeek.weekOf &&
        this.weekMouseOver.weekNumber === week.weekNumber &&
        this.activateWeekDuplication
      );
    },
    formattedDate(date) {
      return moment.utc(date).format('MMM D');
    },
    isToday(weekOf, dayNumber) {
      const dayDate = moment(weekOf).add(dayNumber, 'days');
      return moment().isSame(dayDate, 'day');
    },
    isThisWeek(date) {
      return moment(date).isSame(moment(), 'week'); // false, different month
    },
    scrollToCurrentWeek() {
      // scroll to top of current week if client program and more that 4 weeks in the program
      const parent = document.getElementsByClassName('content-wrapper');
      const targetWeek = document.getElementsByClassName('thisWeek');
      if (
        targetWeek &&
        targetWeek.length > 0 &&
        targetWeek[0] &&
        targetWeek[0].getClientRects() &&
        targetWeek[0].getClientRects().length > 0 &&
        targetWeek[0].getClientRects()[0].top &&
        parent &&
        parent.length > 0 &&
        parent[0] &&
        this.programWeeks &&
        this.programWeeks.length > 4
      ) {
        parent[0].scrollTop =
          targetWeek[0].getClientRects()[0].top -
          (targetWeek[0].clientHeight * 3 - 10);
      }
    },
    deselectWeek() {
      if (this.selectedAllWeek && this.selectedAllWeek.weekOf) {
        const prevCheck = document.getElementById(this.selectedAllWeek.weekOf);
        prevCheck.checked = false;
      }
      this.setSelectedAllWeek(null);
      this.$emit('set-activate-week-duplication', false);
    },
    handleDrop(targetedData, event, destinationData) {
      // Make sure we have clone data before any state changes
      const targetedDataClone = cloneDeep(targetedData);
      const destinationDataClone = cloneDeep(destinationData);
      // Do not move if same week and same day
      if (
        targetedDataClone.day.dayNumber ===
          destinationDataClone.day.dayNumber &&
        targetedDataClone.week.weekNumber ===
          destinationDataClone.week.weekNumber
      ) {
        this.dragenterWeekNum = null;
        this.dragenterDayNum = null;
        console.warn('Can not move to the same day');
      } else {
        switch (targetedData.type) {
          case 'workout':
            if (
              destinationDataClone.day &&
              destinationDataClone.day.workouts &&
              destinationDataClone.day.workouts[0] &&
              !destinationDataClone.day.workouts[0].hasWorkout
            ) {
              this.handleDropWorkout(targetedDataClone, destinationDataClone);
            } else {
              this.setDragModalCtx({
                targetedDataClone,
                destinationDataClone,
              });
              this.$bvModal.show('drag-confirmation-modal');
              this.dragenterWeekNum = null;
              this.dragenterDayNum = null;
            }
            break;
          case 'checkIn':
            this.handleDropCheckin(targetedDataClone, destinationDataClone);
            break;
          case 'note':
            this.handleDropNote(targetedDataClone, destinationDataClone);
            break;
          default:
            return '';
        }
      }
    },
    async handleDropNote(targetedDataClone, destinationDataClone) {
      // swap the the note to the new day so user can see right away
      this.setMoveProgramNote({
        targetedDataClone,
        destinationDataClone,
      });
      this.dragenterWeekNum = null;
      this.dragenterDayNum = null;

      // prep the payload
      const {
        week: {
          weekIndex,
          trainer_id: trainerId,
          org_id: orgId,
          user_id: userId,
        },
        day: { dayIndex, programDay_id: targetedDayId },
      } = targetedDataClone;

      const getTargetedDayPayload = {
        dayId: targetedDayId,
        trainerId,
        orgId,
        userId,
      };

      if (trainerId && orgId && userId) {
        this.movingItem = true;
        try {
          // grab the target day
          const getTargetedDayResponse = await this.getAllDay(
            getTargetedDayPayload
          );

          // if note available at target
          if (
            getTargetedDayResponse &&
            getTargetedDayResponse.success &&
            getTargetedDayResponse.programDay.trainerNote
          ) {
            // create the note in destination
            const {
              week: { weekNumber, program_id: programId },
              day: { dayNumber },
            } = destinationDataClone;
            const payload = {
              source: {
                type: 'trainerNote',
                message: getTargetedDayResponse.programDay.trainerNote,
              },
              programDestination: {
                weekNumber,
                dayNumber,
              },
            };
            const action = 'move';
            const sameWeek =
              targetedDataClone.week.weekNumber ===
              destinationDataClone.week.weekNumber;
            const trainerNoteObj = {
              programId,
              payload,
              action,
              weekIndex,
              dayIndex,
              sameWeek,
            };
            const createNoteResponse = await this.addUpdateTrainerNote(
              trainerNoteObj
            );
            // if note created in new day, delete note from old day
            if (createNoteResponse) {
              // delete the note from the target
              const {
                week: { _id: weekId, program_id: programId },
                day: { programDay_id: dayId },
              } = targetedDataClone;
              const payload = { programId, weekId, dayId };
              await this.deleteTrainerNote(payload);
            } else {
              // move it back
              this.setMoveProgramNote({
                targetedDataClone: destinationDataClone,
                destinationDataClone: targetedDataClone,
              });
            }
          }
        } catch (e) {
          console.error(e);
          // move it back
          this.setMoveProgramNote({
            targetedDataClone: destinationDataClone,
            destinationDataClone: targetedDataClone,
          });
        }
        this.movingItem = false;
      }
      this.dragWeekOver = null;
    },
    async handleDropCheckin(targetedDataClone, destinationDataClone) {
      // swap the the checkin to the new day so user can see right away
      this.setMoveProgramCheckin({
        targetedDataClone,
        destinationDataClone,
      });
      this.dragenterWeekNum = null;
      this.dragenterDayNum = null;

      // prep the payload
      const {
        week: { trainer_id: trainerId, org_id: orgId, user_id: userId },
        day: { programDay_id: targetedDayId },
      } = targetedDataClone;

      const getTargetedDayPayload = {
        dayId: targetedDayId,
        trainerId,
        orgId,
        userId,
      };

      if (trainerId && orgId && userId) {
        this.movingItem = true;
        try {
          // grab the target day
          const getTargetedDayResponse = await this.getAllDay(
            getTargetedDayPayload
          );

          // if checkin available at target and no check in at destination
          if (
            getTargetedDayResponse &&
            getTargetedDayResponse.success &&
            getTargetedDayResponse.programDay.checkIns.length > 0 &&
            !destinationDataClone.day.checkIns[0].scheduledCheckIn_id
          ) {
            // create the checkin in destination
            const { day, week } = destinationDataClone;
            const createCheckinResponse = await this.createCheckin(
              day,
              week,
              targetedDataClone,
              'move'
            );

            // if checkin created in new day, delete checkin from old day
            if (createCheckinResponse) {
              // delete the checkin from the target
              const { day, week, checkinItem } = targetedDataClone;
              await this.deleteCheckin(checkinItem, day, week);
            } else {
              // move it back
              this.setMoveProgramCheckin({
                targetedDataClone: destinationDataClone,
                destinationDataClone: targetedDataClone,
              });
            }
          }
        } catch (e) {
          console.error(e);
          // move it back
          this.setMoveProgramCheckin({
            targetedDataClone: destinationDataClone,
            destinationDataClone: targetedDataClone,
          });
        }

        this.movingItem = false;
      }
      this.dragWeekOver = null;
    },
    async handleDropWorkout(targetedDataClone, destinationDataClone) {
      // Clear class name and swap workouts between days
      this.setMoveProgramWorkout({
        targetedDataClone,
        destinationDataClone,
      });
      this.dragenterWeekNum = null;
      this.dragenterDayNum = null;

      // Prepare the payload to get the day and then the full workout opbject
      const trainerId = targetedDataClone.week.trainer_id;
      const orgId = targetedDataClone.week.org_id;
      const userId = targetedDataClone.week.user_id;

      const targetedDayId = targetedDataClone.day.programDay_id;
      const destinationDayId = destinationDataClone.day.programDay_id;

      const getTargetedDayPayload = {
        dayId: targetedDayId,
        trainerId,
        orgId,
        userId,
      };
      const getDestinationDayPayload = {
        dayId: destinationDayId,
        trainerId,
        orgId,
        userId,
      };

      if (trainerId && orgId && userId) {
        this.movingItem = true;
        try {
          // Grab the day to be abe to access the full workout
          const getTargetedDayResponse = await this.getAllDay(
            getTargetedDayPayload
          );
          if (
            // If Full workout available
            getTargetedDayResponse &&
            getTargetedDayResponse.success &&
            getTargetedDayResponse.programDay.workouts.length > 0
          ) {
            if (
              // We need to delete the current workout in the destination day before creating a new wokrout
              destinationDataClone.day &&
              destinationDataClone.day.workouts &&
              destinationDataClone.day.workouts[0] &&
              destinationDataClone.day.workouts[0].hasWorkout
            ) {
              const getDestinationDayResponse = await this.getAllDay(
                getDestinationDayPayload
              );
              const workout = getDestinationDayResponse.programDay.workouts[0];
              const payload = {
                programId: this.selectedProgram._id,
                dayId: destinationDataClone.day.programDay_id,
                weekId: destinationDataClone.week._id,
                workoutId: workout.programWorkout_id,
                isCustom: workout.isCustom || false,
                type: 'move',
              };
              await this.deleteWorkoutDay(payload);
            }

            // Now we start creating the workout in the destination day slot
            const createWorkoutPayload =
              getTargetedDayResponse.programDay.workouts[0];
            const createWorkoutResponse = await this.createProgramWorkout(
              createWorkoutPayload,
              destinationDataClone,
              targetedDataClone
            );
            if (
              // If workout created on the new day then go ahead and delete the old workoutDay
              createWorkoutResponse &&
              createWorkoutResponse.status === 200
            ) {
              const workout = createWorkoutPayload;
              const payload = {
                programId: this.selectedProgram._id,
                dayId: targetedDataClone.day.programDay_id,
                weekId: targetedDataClone.week._id,
                workoutId: workout.programWorkout_id,
                isCustom: workout.isCustom || false,
              };
              await this.deleteWorkoutDay(payload);
            } else {
              // If fails then set the state back to before the call
              this.setMoveProgramWorkout({
                targetedDataClone: destinationDataClone,
                destinationDataClone: targetedDataClone,
                type: 'reverse',
              });
              this.movingItem = false;
            }
          } else {
            this.movingItem = false;
          }
          this.movingItem = false;
        } catch (e) {
          console.log(e);
          this.setMoveProgramWorkout({
            targetedDataClone: destinationDataClone,
            destinationDataClone: targetedDataClone,
            type: 'reverse',
          });
          this.movingItem = false;
        }
      }
      this.setDragModalCtx(null);
      this.dragWeekOver = null;
    },
    createProgramWorkout(payload, destinationDataClone, targetedDataClone) {
      return this.addUpdateWorkoutDay({
        programId: this.selectedProgram._id,
        payload: {
          programDestination: {
            weekNumber: destinationDataClone.week.weekNumber,
            dayNumber: destinationDataClone.day.dayNumber,
          },
          source: {
            type: 'workout',
            programWorkout_id: payload.programWorkout_id || null,
            programWorkout: { ...payload },
          },
        },
        action: 'move',
        sameWeek:
          targetedDataClone.week.weekNumber ===
          destinationDataClone.week.weekNumber,
        targetedDataClone,
        destinationDataClone,
      });
    },
    dragenter(week, day) {
      this.dragenterDayNum = day.dayNumber;
      this.dragenterWeekNum = week.weekNumber;
    },
  },
  async beforeDestroy() {
    this.setSelectedAllWeek(null);
    this.setAllWorkouts([]);
  },
};
</script>

<template>
  <div class="program-calendar">
    <NotificationProgramCalendar
      v-if="duplicationDayActive || activateWeekDuplication"
      :content="duplicationDayActive ? 'day' : 'week'"
      :isMouseOverNotification="
        duplicationDayActive
          ? weekMouseOver.weekNumber === 1 &&
            (dayMouseOver.dayNumber === 1 ||
              dayMouseOver.dayNumber === 2 ||
              dayMouseOver.dayNumber === 3)
          : weekMouseOver.weekNumber === 1
      "
      @cancel-duplication="
        () => (duplicationDayActive ? duplicateDayCancel() : deselectWeek())
      "
    />

    <!-- TODO:: Widdle this down to even more nested components -->
    <template v-for="(week, weekIndex) in programWeeks">
      <div
        :key="`${week.weekOf}week-banner`"
        :class="['week-banner', { thisWeek: isThisWeek(week.weekOf) }]"
      >
        {{
          isClientProgram
            ? `Week of ${formattedDate(week.weekOf)}`
            : `Week ${week.weekNumber}`
        }}
      </div>
      <div
        @mouseover="
          duplicationDayActive || activateWeekDuplication
            ? (weekMouseOver = week)
            : ''
        "
        :class="[
          'program-week-row',
          {
            active: selectedAllWeek && week.weekOf === selectedAllWeek.weekOf,
            isDuplicateToThisWeek: isDuplicateToThisWeek(week),
          },
        ]"
        ref="weekRow"
        :key="week.weekOf"
        ><div
          class="duplicate-week-helper"
          @click="onDuplicateToAWeek(week)"
          v-if="isDuplicateToThisWeek(week) && activateWeekDuplication"
        >
          <span v-if="activateWeekDuplication"
            >Duplicate to week {{ week.weekNumber }}
          </span>
          <BaseIcon
            v-if="copyingWeek"
            name="sync"
            spin
            class="week-duplicating-icon"
            src="@assets/images/icon-trash.svg"
          />
        </div>
        <div class="week-selector">
          <div class="day-header">
            <div class="type-opts-container">
              <label v-if="allowEditing" :for="week.weekOf">
                <input
                  type="checkbox"
                  :id="week.weekOf"
                  :value="week"
                  @change="(e) => highlightAllWeek(e, week)"
                />
                <span class="check-box"></span>
              </label> </div
          ></div>
        </div>
        <template v-for="(day, index) in week.days">
          <div
            @mouseover="dragWeekOver = week"
            @mouseleave="dragWeekOver = null"
            :class="[
              'program-day-col',
              {
                hover:
                  !movingItem &&
                  dayMouseOver &&
                  dayMouseOver.dayNumber &&
                  day &&
                  day.dayNumber &&
                  day.dayNumber === dayMouseOver.dayNumber &&
                  week &&
                  week.weekNumber &&
                  dragWeekOver &&
                  dragWeekOver.weekNumber &&
                  week.weekNumber === dragWeekOver.weekNumber,
              },
            ]"
            :key="index"
            :day="day"
          >
            <Drop
              :class="[
                'drop',
                {
                  dropHere:
                    week.weekNumber === dragenterWeekNum &&
                    day.dayNumber === dragenterDayNum,
                },
              ]"
              @dragenter="dragenter(week, day)"
              @drop="
                (data, event) =>
                  handleDrop(data, event, {
                    week: { weekIndex, ...week },
                    day: { dayIndex: index, ...day },
                  })
              "
            >
              <div
                class="day-content-container"
                @mouseover="dayMouseOver = day"
                @mouseleave="viewActions = false"
              >
                <div
                  :class="['day-header', { 'allow-editing': allowEditing }]"
                  ref="dayHeader"
                  ><span
                    :class="[
                      'day-number',
                      {
                        today:
                          isToday(week.weekOf, day.dayNumber) &&
                          isClientProgram,
                      },
                    ]"
                    >{{ getDayNumber(week.weekNumber, day.dayNumber) }}</span
                  >
                  <span
                    v-show="!duplicationDayActive && allowEditing"
                    v-if="
                      !selectedAllWeek ||
                        (week.weekOf !== selectedAllWeek.weekOf &&
                          !isDuplicateToThisWeek(week))
                    "
                    class="icon-add-wrapper"
                    ref="dayPlus"
                    @click="toggleActionList(week, day)"
                  >
                    <div
                      :class="[
                        'icon-add',
                        {
                          selected:
                            selectedDay &&
                            selectedDay.dayNumber &&
                            getDayNumber(week.weekNumber, day.dayNumber) ===
                              selectedDay.calculatedDayNumber &&
                            viewActions,
                        },
                      ]"
                    >
                      <div />
                      <div />
                      <div />
                    </div>
                  </span>
                </div>
                <CalendarSlotCopy
                  :copyingDay="copyingDay"
                  @duplicate-day-end="duplicateDayEnd(day, week)"
                  v-show="isEqualData(day, week)"
                  v-if="duplicationDayActive"
                  :dayHasContent="dayHasContent(day, week)"
                />
                <div
                  :class="[
                    'day-content',
                    { isDuplicateToThisWeek: isDuplicateToThisWeek(week) },
                  ]"
                >
                  <CalendarSlotTooltip
                    v-if="
                      selectedDay &&
                        selectedDay.dayNumber &&
                        getDayNumber(week.weekNumber, day.dayNumber) ===
                          selectedDay.calculatedDayNumber &&
                        viewActions
                    "
                  >
                    <CalendarTooltipActionList
                      :isClientProgram="isClientProgram"
                      :day="day"
                      @add-workout="addWorkout(day, week)"
                      @create-note="viewNote(day, week)"
                      @create-checkin="createCheckin(day, week, day, week)"
                      @duplicate-day-start="duplicateDayStart(day, week)"
                      @clear-all-day-events="onClearAllDayEvents(day, week)"
                    />
                  </CalendarSlotTooltip>
                  <Drag
                    v-for="workout in day.workouts"
                    :key="workout.label"
                    class="drag"
                    :data="workout"
                    :draggable="
                      allowEditing &&
                        !movingItem &&
                        workout.clientInput.startedAt === ''
                    "
                    :transfer-data="{
                      type: 'workout',
                      week: { weekIndex, ...week },
                      day: { dayIndex: index, ...day },
                      workout,
                    }"
                  >
                    <CalendarSlotBasic
                      v-if="workout.hasWorkout || workout.programWorkout_id"
                      :key="workout.label"
                      @view-workout="viewWorkout(workout, day, week)"
                      :week="week"
                      type="workout"
                      :content="
                        workout && workout.label
                          ? workout.label
                          : `${getDayName(week, day)}'s Workout`
                      "
                      :workout="workout"
                      :allowEditing="allowEditing"
                    />
                  </Drag>
                  <Drag
                    class="drag"
                    v-if="day.dayHasTrainerNote"
                    :data="day.dayHasTrainerNote"
                    :draggable="
                      allowEditing && !movingItem && day.dayHasTrainerNote
                    "
                    :transfer-data="{
                      type: 'note',
                      week: { weekIndex, ...week },
                      day: { dayIndex: index, ...day },
                    }"
                  >
                    <CalendarSlotBasic
                      v-if="day.dayHasTrainerNote"
                      @view-note="viewNote(day, week)"
                      :week="week"
                      type="note"
                      content="Note"
                      :allowEditing="allowEditing"
                    />
                  </Drag>
                  <Drag
                    v-for="checkinItem in day.checkIns"
                    :key="checkinItem.scheduledCheckIn_id"
                    class="drag"
                    :data="checkinItem"
                    :draggable="
                      allowEditing && !movingItem && !checkinItem.isComplete
                    "
                    :transfer-data="{
                      type: 'checkIn',
                      week: { weekIndex, ...week },
                      day: { dayIndex: index, ...day },
                      checkinItem,
                    }"
                  >
                    <CalendarSlotBasic
                      :key="checkinItem.scheduledCheckIn_id"
                      :checkinId="checkinItem.scheduledCheckIn_id"
                      v-if="
                        day.checkIns.length > 0 &&
                          checkinItem.scheduledCheckIn_id
                      "
                      :week="week"
                      :deletingCheckin="deletingCheckin"
                      :checkinItem="checkinItem"
                      @delete-checkin="
                        !checkinItem.isComplete
                          ? deleteCheckin(checkinItem, day, week)
                          : null
                      "
                      :allowEditing="allowEditing"
                      type="checkin"
                      content="Check-in"
                    />
                  </Drag>
                </div>
              </div>
            </Drop>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown
// stylelint-disable no-descending-specificity

.week-banner {
  align-items: center;
  background-color: $color-off-white;
  display: flex;
  font-family: $base-font-family;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  height: 3rem;
  line-height: 50px;
  padding-left: 3rem;
  position: sticky;
  top: 0;
  z-index: 3;
}

.program-day-col {
  @include calc-font-size(1.6);

  display: flex;
  list-style: none;
  margin-left: 0;
  min-height: 124px;
  padding-bottom: 0.4rem;
  transition: all linear 0.8s;
  width: 80%;

  &:not(:last-child) {
    border-right: 1px solid $color-light-gray;
  }
  .drop {
    position: relative;
    width: 100%;
    &.dropHere {
      &::after {
        background: rgba($color-primary-one-wo-rgb, 0.1);
        border: 2px solid $color-action;
        bottom: 0;
        content: '';
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
      }
    }
  }
  .day-header {
    padding-right: 1rem;
    transition: all 0.23s ease-in;
    .icon-add-wrapper {
      display: none;
      .icon-add {
        cursor: pointer;
        height: fit-content;
        opacity: 1;
        padding-bottom: 0.5rem;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        padding-top: 0.5rem;
        transition: all 300ms;
        div {
          background-color: $color-off-white;
          border-radius: 50%;
          height: 4px;
          margin-bottom: 2px;
          width: 4px;
        }
        &:hover {
          div {
            background-color: rgba($color-off-white-wo-rgb, 0.8);
          }
        }
        &.selected {
          div {
            background-color: rgba($color-off-white-wo-rgb, 0.8);
          }
        }
      }
    }
  }
  .day-content {
    .calendar-slot-tooltip {
      display: none;
    }
  }
  &.hover {
    .day-header {
      &.allow-editing {
        align-items: center;
        background-color: $color-action;
        display: flex;
        justify-content: space-between;
        .day-number {
          color: $color-off-white;
        }
        .icon-add-wrapper {
          display: block;
          .icon-add {
            opacity: 1;
          }
        }
      }
    }
    .day-content {
      .calendar-slot-tooltip {
        display: block;
        z-index: 4;
      }
    }
  }
}

.program-calendar {
  background-color: white;
  border-bottom: 1px solid $color-light-gray;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  .week-selector {
    border-right: 1px solid $color-light-gray;
  }
  .program-week-row {
    border-top: 1px solid $color-light-gray;
    display: flex;
    margin: 0;
    max-width: 100%;
    padding: 0;
    position: relative;
    .duplicate-week-helper {
      @include calc-font-size(7.6);

      align-items: center;
      background-color: rgba($color-black-wo-rgb, 0.1);
      color: rgba($color-black-wo-rgb, 0.4);
      cursor: pointer;
      display: flex;
      font-weight: 900;
      height: 100%;
      justify-content: center;
      position: absolute;
      width: 100%;
      z-index: 2;
      span {
        margin-right: 3rem;
      }
    }
    .week-selector {
      .day-header {
        padding: 0.5rem 0.7rem 0 0.7rem;
      }
    }

    .day-content {
      &.isDuplicateToThisWeek {
        opacity: 0.2;
      }
      .calendar-slot-basic {
        &.week-selected {
          background-color: $color-primary;
          color: white;
        }
      }
    }

    .day-content-container {
      height: 100%;
      position: relative;
      width: 100%;
      &:not(:hover) {
        .day-number {
          &.today {
            border-radius: 50%;
            color: black;
            font-weight: 800;
            margin-left: 0.5rem;
            padding-left: 0.5rem;
            padding-right: 0.6rem;
            padding-top: 1.5rem;
            width: fit-content;
          }
        }
      }
      // stylelint-disable single-line-max-declarations
      .day-header {
        margin-bottom: 0.5rem;
        .day-number {
          display: block;
          font-size: 1.7rem;
          padding: 1rem;
          padding-left: 1.5rem;
          padding-top: 1.5rem;
        }
      }

      .day-content {
        padding: 0 0.4rem;
      }
    }

    &.active {
      .week-selector {
        background-color: rgba($color-primary-wo-rgb, 0.2);
        transition: all linear 0.3s;
      }
      .day-content-container {
        background-color: rgba($color-primary-wo-rgb, 0.2);
        transition: all linear 0.3s;
      }
      .day-header {
        background-color: rgba($color-primary-wo-rgb, 0.4);
        transition: all linear 0.3s;
      }
      .program-day-col {
        &:not(:last-child) {
          border-right: 1px solid rgba($color-black-wo-rgb, 0.2);
        }
      }
    }
  }

  // stylelint-disable selector-max-type
  label {
    position: relative;
  }
  .check-box-title {
    color: $color-heading-text;
    font-size: 1rem;
    font-weight: 900;
    letter-spacing: 0.2px;
    line-height: 2rem;
    margin-left: 7px;
    text-transform: capitalize;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  .type-opts-container {
    display: inline-flex;
    justify-content: center;
    margin-bottom: 0;
    margin-top: 0.8rem;
    label {
      margin-bottom: 0.2rem;
    }
  }
  .check-box {
    background-color: $color-light-gray;
    border-radius: 10%;
    cursor: pointer;
    display: inline-block;
    height: 14px;
    transition: all linear 0.3s;
    width: 14px;
    &::after {
      border-bottom: 2px solid $color-tag-text;
      border-right: 2px solid $color-tag-text;
      content: '';
      height: 9px;
      left: 5px;
      opacity: 0.6;
      position: absolute;
      top: 2.5px;
      transform: rotate(45deg);
      visibility: hidden;
      width: 5px;
    }
  }
  input {
    display: none;
    &:checked ~ .check-box {
      background: $color-primary;
      &::after {
        visibility: visible;
      }
    }
  }
}
</style>
